/* eslint-disable no-restricted-syntax */
/* eslint-disable no-prototype-builtins */
import Highcharts from 'highcharts';
import moment from 'moment';
import {
  ColumnDataTypes,
  ColumnRenderTypes,
  DimensionNoValue,
  DimensionNumericNoValue,
  EncryptedText,
  Others,
  PivotColGrpHeaderSeparator,
  RotationValues,
  RowGroupingLevel,
} from 'core/constants/report';
import { ObjModel, ReportResponseModel } from 'core/models';
import { ChartDefaultcolors, VisualizationTypes } from 'core/constants/visualizations';
import _ from 'lodash';
import { Formatters, DateTimeFormatters } from 'core/constants/formatters';
import {
  IAggregatesData, IColumn, IField, IFiltersMetaData, IFilterConfig,
} from 'core/models/report-response';
import { IChartAttributes } from 'core/models/report-redux';
import { drilldownColumnDelimiter } from 'core/constants/common';
import { getFetchDrilldownReport, saveLinkedInsightDrilldown } from 'redux-v2/report/report-store.requests';
import { IFilterResponse } from 'core/models/filter';
import { Dispatch } from '@reduxjs/toolkit';
import { reportAutodrilldown } from 'redux-v2/report/report-store.reducer';
import { getTime, parseISO } from 'date-fns';
import { getEntityJoinIdForMeasure } from 'components/feature/ReportBuilderV2/ReportBuilderDetail/DrilldownTabContent/drilldown.util';
import { IBuilderDimension, IReportConfig } from 'core/models/report-builder/report-builder';
import { ILinkedConfig } from 'redux-v2/report-builder/report-builder-store.state';
import { PanelSourceTypes } from 'core/constants/dashboard';
import { FilterType } from 'core/constants/filter';
import { userStatusEnum } from 'components/feature/Report/ReportSidebar/common/constants';
import { getSelectedRowData } from './report-builder.util';
import {
  isMeasure, isMeasureDynamic, isColumnReferredCol,
  getDefaultEntity,
} from './report.util';
import { getFormattedValues, getFormattedValuesForBinning } from './formatters.utility';
import { convertEpocToDate } from './date.util';

export interface ILinkedInsightChartProps {
  linkedReportsConfig: ILinkedConfig[];
  activeFilters: ObjModel.ObjGeneric<IFilterResponse>;
  filterConfig: IFilterConfig[];
  reportId: string;
  filtersMetadata: IFiltersMetaData;
  onWindowOpen: (window: Window | null) => void;
  isMarvin?: boolean;
  isMobileApp?: boolean;
}

export interface IChartUtilityProps {
  builder: ReportResponseModel.IChartVisualizationBuilder,
  columns: ReportResponseModel.IColumn[],
  reportData: ObjModel.Obj[],
  chartType: VisualizationTypes,
  isDashboardCharts: boolean
  allowDrilldownOnMaskedDimensions?: boolean,
  reportName?: string,
  styleConfig?: any,
  dispatch?:Dispatch<any>,
  disableDrilldown?: boolean,
  onDrilldownClickDashboard?: (ClickedRowData: Object, selectedMeasure: IColumn, rowTotalClicked: boolean) => void,
  chartHeight?: number,
  chartWidth?: number,
  attributes?: IChartAttributes,
  isZeroValuesEnabled?:boolean,
  aggregatesData?: IAggregatesData,
  fields?: Array<IField>,
  savedReportConfig?: IReportConfig,
  showTooltip?: boolean,
  linkedInsightProps?: ILinkedInsightChartProps,
}

export interface IColumnChartYAxis {
  data: number,
  field: string,
}

export const GetChartColors = (chartType: VisualizationTypes, styleConfig:any, color?: number) => {
  // below values will be from config line {this.data.reportConfig.Visualization.chartcolors}
  const chartColors = styleConfig ? styleConfig.Colors : null;
  switch (chartType) {
    case VisualizationTypes.ColumnChart:
    case VisualizationTypes.BarChart:
    case VisualizationTypes.AreaChart:
    case VisualizationTypes.LineChart: {
      const colors = chartColors || ChartDefaultcolors;
      return colors[color % 10];
    }
    default: {
      return chartColors || ChartDefaultcolors;
    }
  }
};

export const ChartsBaseTheme: Highcharts.Options = {
  chart: {
    backgroundColor: 'var(--chartBackground)',
  },
  title: {
    style: {
      color: 'var(--chartBaseColor)',
      font: '12px "SemiBold"',
    },
    align: 'left',
  },
  subtitle: {
    style: {
      color: 'var(--base-font-color)',
      font: '12px "Regular"',
    },
  },
  legend: {
    itemStyle: {
      font: '10px Regular',
      color: 'var(--chartBaseColor)',
      cursor: 'pointer',
    },
    itemHoverStyle: {
      color: 'gray',
    },
  },
};

export const getChartHeight = (groupingLevel: number) => {
  switch (groupingLevel) {
    case RowGroupingLevel.Three: {
      return 600;
    }
    case RowGroupingLevel.Two: {
      return 500;
    }
    default: {
      return 400;
    }
  }
};

export const getGroupedCategoriesStyle = (chartType:VisualizationTypes, isDashboardChart: boolean) => {
  const groupedStyle = {
    style: {
      textOverflow: 'ellipsis',
      width: isDashboardChart ? 50 : 75,
      padding: 0,
    },
    showEmpty: false,
    rotation: RotationValues.vertical,
    padding: 0,
    useHTML: false,
    x: 2,
    y: 2,
  };
  if (chartType === VisualizationTypes.BarChart) {
    groupedStyle.rotation = RotationValues.horizontal;
    groupedStyle.x = 0;
    groupedStyle.y = 0;
  }
  return groupedStyle;
};

export const getFormattedAxisLabels = (column:ReportResponseModel.IColumn, field:ObjModel.Obj, allowDrilldownOnMaskedDimensions:boolean, fieldValue?:string) => {
  const { Props } = column;
  const value = fieldValue || field[column.Id];
  const renderType = isMeasure(column) ? column?.Props?.Measure?.RenderType : column?.BuilderConfig?.RenderType;
  if (isColumnReferredCol(column)) {
    const parentDimension = column?.Props.ParentDimension;
    if ((Array.isArray(field) && field[0][parentDimension] === Others) || field[parentDimension] === Others) return 'Others';
  }
  if (Props?.IsMasked) {
    if (allowDrilldownOnMaskedDimensions) {
      return `${value}${PivotColGrpHeaderSeparator}${EncryptedText}`;
    }
    return EncryptedText;
  }
  // For metafields we are null as string so setting [No Value]
  // can't check type is not a number due to the condition after below if statements, that condition will throw error as check not a number will convert it to number coercion
  if (value === 'null' || value === undefined || (Props.Dimension?.DimensionProp?.Props?.BinningType && value === '0')) {
    return DimensionNoValue;
  }

  if (value === null) {
    return DimensionNumericNoValue;
  }

  if (value === Others) {
    return value.slice(1, -1);
  }

  return getFormattedValueForAxisLabel(value, column, renderType);
};

const getFormattedValueForAxisLabel = (
  value: string,
  column: ReportResponseModel.IColumn,
  renderType?: ColumnRenderTypes,
) => (column.Props?.Dimension?.DimensionProp?.DataType === ColumnDataTypes.DateTime
    && column.Props.Dimension?.DimensionProp?.Props?.BinningType
  ? getFormattedValuesForBinning(value, column.Props.Dimension.DimensionProp.Props.BinningType)
  : getFormattedValues(value, column.Props.Formatter, undefined, true, renderType));

export const getAxisLabels = (groupingLevel: number, chartType:VisualizationTypes, isDashboardChart: boolean) => {
  const labelObject = {
    useHTML: false,
    showEmpty: false,
    padding: 0,
    tickColor: 'var(--paginationBorder)',
    tickWidth: 1,
    style: {
      textOverflow: 'ellipsis',
      width: isDashboardChart ? 50 : 75,
      padding: 0,
    },
    groupedOptions: [getGroupedCategoriesStyle(chartType, isDashboardChart), getGroupedCategoriesStyle(chartType, isDashboardChart)],
    rotation: RotationValues.horizontal,
    x: 0,
  };
  if (groupingLevel === RowGroupingLevel.One) {
    labelObject.rotation = isDashboardChart ? RotationValues.horizontal : RotationValues.autoRotate;
  }
  if (chartType === VisualizationTypes.BarChart) {
    labelObject.rotation = RotationValues.horizontal;
    labelObject.x = -5;
  }
  return labelObject;
};

export const getChartTooltipHeader = (groupingLevel: number, key:string, keyName:string, levelOneParentName:string, levelTwoParentName:string) => {
  switch (groupingLevel) {
    case RowGroupingLevel.One: {
      return `<div class="highchart-tooltip-header">${key}</div>`;
    }
    case RowGroupingLevel.Two: {
      return `<div class="highchart-tooltip-header">${levelOneParentName} - ${keyName}</div>`;
    }
    default: {
      return `<div class="highchart-tooltip-header">${levelTwoParentName} - ${levelOneParentName} - ${keyName}</div>`;
    }
  }
};

export const getTooltipContent = (seriesColor:string, seriesName:string, pointValue:number, formatter:ReportResponseModel.IColumnFormatter) => {
  const formattedValue = getFormattedValues(pointValue, formatter, undefined, false);
  return (
    `<div class="highcharts-tooltip-cn"><div class="highchart-circle-cn">
<div class="point-format" style="background:${seriesColor};color: ${seriesColor}"></div>
</div><div class="highchartooltips">${seriesName} : </div>
<div class="value">${formattedValue}</div></div>`
  );
};

export const groupBy = (array: any[], key: string | number) => array.reduce((result, currentValue) => {
  // If an array already present for key, push it to the array. Else create an array and push the object
  // adding a delimiter to preserve the sort order if key comes as integer
  const currentKey = `${currentValue[key]}${drilldownColumnDelimiter}`;
  // eslint-disable-next-line no-param-reassign
  (result[currentKey] = result[currentKey] || []).push(
    currentValue,
  );
  // Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
  return result;
}, {}); // empty object is the initial value for result object

export const combineDataonLastGrouping = (rowdata: ObjModel.Obj[], column: ReportResponseModel.IColumn, XAxis : ReportResponseModel.IColumn[], ColumnFields : ReportResponseModel.IColumn[]) => {
  if (ColumnFields?.length === 1 && column === XAxis[XAxis.length - 1]) {
    const columnGroupedData: ObjModel.Obj[] = [];
    groupByColumn(rowdata, column.Id)
      .forEach((field) => {
        columnGroupedData.push(field[0]);
      });
    return columnGroupedData;
  }

  return rowdata;
};

export const getLabelMaxHeight = (groupingLevel: number) => {
  switch (groupingLevel) {
    case RowGroupingLevel.Three: {
      return 530;
    }
    case RowGroupingLevel.Two: {
      return 450;
    }
    default: {
      return 360;
    }
  }
};

// Whether a measure is numeric for charting purposes. Mirrors PieChartUtil.setChartData's rule:
// a measure is treated as numeric unless its formatter marks it String/Date/Time; a measure with no
// formatter is allowed. Used to keep non-numeric measures out of magnitude-based charts (pie/funnel).
export const isNumericMeasure = (column: ReportResponseModel.IColumn): boolean => {
  const formatterType = column?.Props?.Formatter?.FormatterType;
  return formatterType !== Formatters.STRING
    && formatterType !== Formatters.DATE
    && formatterType !== Formatters.TIME;
};

export const getUpdatedFormatter = (currentFormatter: ReportResponseModel.IColumnFormatter) => {
  let updatedFormatter = currentFormatter;
  if (currentFormatter?.FormatterType === Formatters.NUMBER) {
    updatedFormatter = {
      ...currentFormatter,
      FormatterType: Formatters.INT_NUMBER,
    };
  }
  return updatedFormatter;
};

export const getFormattedValuesForCharts = (value:string, column:ReportResponseModel.IColumn, allowDrilldownOnMaskedDimension:boolean) => {
  const { Props } = column;
  const renderType = isMeasure(column) ? column?.Props?.Measure?.RenderType : column?.BuilderConfig?.RenderType;
  if (Props?.IsMasked) {
    if (allowDrilldownOnMaskedDimension) {
      return `${value}${PivotColGrpHeaderSeparator}${EncryptedText}`;
    }
    return EncryptedText;
  }
  return Props.Dimension?.DimensionProp?.Props.BinningType
    ? getFormattedValuesForBinning(value, Props.Dimension.DimensionProp.Props.BinningType)
    : getFormattedValues(value, getUpdatedFormatter(column.Props.Formatter), undefined, true, renderType);
};
export const autoDrilldownFromCharts = (selectedRow : any, dispatch : Dispatch<any>, column: ReportResponseModel.IColumn, rowTotalClicked: boolean, fields?: Array<IField>, savedReportConfig?: IReportConfig) => {
  if (dispatch) {
    const measureField = getEntityJoinIdForMeasure(fields, column, getDefaultEntity(savedReportConfig));
    dispatch(reportAutodrilldown({
      selectedMeasure: column.Id,
      selectedRowData: selectedRow,
      measureFieldAlias: isMeasureDynamic(column) ? column.BuilderConfig.Alias : '',
      rowPinnedClicked: false,
      clickedOnColumnTotal: false,
      clickedOnRowTotal: rowTotalClicked,
    }));
    dispatch(getFetchDrilldownReport(
      {
        selectedMeasure: column.Id,
        selectedRowData: selectedRow,
        measureFieldAlias: isMeasureDynamic(column) ? column.BuilderConfig.Alias : '',
        rowPinnedClicked: false,
        clickedOnColumnTotal: false,
        clickedOnRowTotal: rowTotalClicked,
        selectedMeasureInfo: measureField,
      },
    ));
  }
};

export const handleDrilldownClick = (
  isDashboardCharts: boolean,
  column: IColumn,
  field: any,
  dispatch: Dispatch<any>,
  drilldownClickDashboard: Function,
  rowTotalClicked: boolean,
  isZeroValuesEnabled: boolean,
  fields?: Array<IField>,
  savedReportConfig?: IReportConfig,
  linkedInsightProps?: ILinkedInsightChartProps,
  reportColumns?: IColumn[],
) => {
  const measureName = column?.Name || column?.BuilderConfig.Alias;
  // when zero values enabled we will disabled auto drilldown on 0 values
  if (measureName in field && field[measureName] === 0 && isZeroValuesEnabled) {
    return;
  }
  if (isDashboardCharts && drilldownClickDashboard) {
    drilldownClickDashboard(field, column, rowTotalClicked);
    return;
  }
  if (linkedInsightProps) {
    const linkedConfig = linkedInsightProps.linkedReportsConfig?.find(
      (cfg) => cfg.MeasureIdList.includes(column.Id),
    );
    if (linkedConfig && dispatch) {
      const linkingFilterIds = Object.keys(linkedConfig.LinkingConfig?.Filters ?? {});
      const linkedFilters = Object.fromEntries(
        Object.entries(linkedInsightProps.activeFilters ?? {})
          .filter(([key]) => linkingFilterIds.includes(key))
          .map(([key, value]) => [key, (value as IFilterResponse).FilterResponse]),
      );
      const activeFilterEntries = Object.fromEntries(
        Object.entries(linkedInsightProps.activeFilters ?? {})
          .map(([key, value]) => [key, (value as IFilterResponse).FilterResponse]),
      );
      const primaryUserFilter = linkedInsightProps.filterConfig?.find(
        (f) => f.Type === FilterType.UserMultiSelect && f.IsPrimaryUserFilter,
      );
      const primaryUserFilterStatus = primaryUserFilter?.Metadata?.Status ?? userStatusEnum.Both;
      if (!linkedInsightProps.isMarvin && !linkedInsightProps.isMobileApp) {
        linkedInsightProps.onWindowOpen(window.open('', '_blank'));
      }
      dispatch(saveLinkedInsightDrilldown({
        payload: {
          SourceId: linkedInsightProps.reportId,
          DataSourceId: linkedConfig.LinkedReportId,
          DataSourceType: PanelSourceTypes.Report,
          Filters: activeFilterEntries ?? {},
          LinkedFilters: linkedFilters,
          SelectedMeasure: column.Id,
          SelectedRow: rowTotalClicked ? [] : getSelectedRowData(reportColumns ?? [], field),
          MeasureFieldAlias: isMeasureDynamic(column) ? column.BuilderConfig.Alias : '',
          DrilldownOnRowTotal: rowTotalClicked,
          FiltersMetadata: { ...linkedInsightProps.filtersMetadata, Status: primaryUserFilterStatus },
          linkedReportId: linkedConfig.LinkedReportId,
          LinkedReportsConfig: linkedInsightProps.linkedReportsConfig,
        },
        targetReportId: linkedConfig.LinkedReportId,
      }) as any);
      return;
    }
  }
  autoDrilldownFromCharts(field, dispatch, column, rowTotalClicked, fields, savedReportConfig);
};

export const getStackedProperty = (isStacked: boolean, isFullStacked: boolean) => {
  if (isFullStacked) {
    return 'percent';
  }
  if (isStacked) {
    return 'stacked';
  }
  return '';
};

export const getLabelsFormat = (showPercent: boolean, showLabels: boolean, showCategoryName: boolean) => {
  let format = '';

  if (showLabels) {
    format += '{point.y}';
  }

  if (showPercent) {
    if (format) format += ' ';
    format += '({point.percentage:.1f}%)';
  }

  if (showCategoryName) {
    if (format) format += ' ';
    format += '{point.name}';
  }

  return format || null;
};

/**
 * Groups a collection of items by a specified property or key
 * @param collection - The array of items to group
 * @param iteratee - The key to group by (string/number) or function to generate group key
 * @returns Map containing grouped items with their respective keys
 */
export const groupByColumn = (collection: Array<ObjModel.Obj>, iteratee: string | number) => {
  const result = new Map();
  const getKey = typeof iteratee === 'function'
    ? iteratee
    : (item: { [x: string]: any; }) => item[iteratee];

  for (const item of collection) {
    const key = getKey(item)?.toString();
    if (!result.has(key)) {
      result.set(key, []);
    }
    result.get(key).push(item);
  }

  return result;
};
